import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiKeyRow {
  id: string;
  name: string;
  key_prefix: string;
  key_last4: string;
  scopes: string[];
  rate_limit_per_min: number;
  expires_at: string | null;
  last_used_at: string | null;
  status: 'active' | 'revoked' | 'expired';
  created_by: string;
  created_at: string;
}

export interface CreateApiKeyInput {
  name: string;
  scopes: string[];
  rate_limit_per_min?: number;
  expires_at?: string | null;
}

export interface CreateApiKeyResult extends ApiKeyRow {
  full_key: string; // returned only once on creation
}

export const ALL_SCOPES = [
  'read:messages',
  'write:messages',
  'read:documents',
  'write:documents',
  'read:calendar',
  'write:calendar',
  'read:tasks',
  'write:tasks',
  'read:people',
  'write:people',
  'read:invoices',
  'write:invoices',
  'read:customers',
  'write:customers',
  'read:inventory',
  'write:inventory',
  'read:quotes',
  'write:quotes',
  'read:tickets',
  'write:tickets',
  'read:attendance',
  'write:attendance',
  'read:employees',
  'admin:public_api',
] as const;

export type ApiScope = (typeof ALL_SCOPES)[number];

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const apiKeyKeys = {
  all: ['api-keys'] as const,
  list: (tenantId: string) => [...apiKeyKeys.all, 'list', tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl ?? '';
  const base = supabaseUrl.replace('/rest/v1', '');
  const res = await fetch(`${base}/functions/v1/api-keys${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useApiKeys(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: apiKeyKeys.list(tenantId),
    queryFn: async () => {
      const result = await apiFetch<{ data: ApiKeyRow[] }>(supabase, '/');
      return result.data;
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateApiKey(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApiKeyInput) =>
      apiFetch<{ data: CreateApiKeyResult }>(supabase, '/', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: apiKeyKeys.list(tenantId) });
    },
  });
}

export function useRevokeApiKey(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => apiFetch<undefined>(supabase, `/${keyId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: apiKeyKeys.list(tenantId) });
    },
  });
}
